import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { notifyError } from '../../HelperMethods';
import debounce from 'lodash/debounce';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

const GOOGLE_API_KEY = "AIzaSyA8Io_sD3d5uTz6ZTj4FKae_I7mdBVjX9o";

const RegistrationAddressFiller = () => {
    const { t } = useTranslation();
    const { values, touched, errors, setFieldValue, handleBlur, validateForm } = useFormikContext();

    const [showMapModal, setShowMapModal] = useState(false);
    const [addressLoading, setAddressLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [addressTimer, setAddressTimer] = useState(null);
    const [latLngTimer, setLatLngTimer] = useState(null);
    const [marker, setMarker] = useState(null);
    const [showInfoWindow, setShowInfoWindow] = useState(false);
    const [selectedInfo, setSelectedInfo] = useState(null);

    const mapRef = useRef(null);

    const suratCenter = { lat: 21.170240, lng: 72.831062 };

    // Load Google Maps script with better handling
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_API_KEY,
        id: 'google-map-script',

    });

    useEffect(() => {
        if (loadError) {
            notifyError("Failed to load Google Maps. Please check your connection.");
        }
    }, [loadError]);

    // ── Forward Geocoding: Address → Lat/Lng ────────────────────────────────
    const fetchLatLngFromAddress = useCallback(
        debounce(async (address) => {
            if (!address || address.trim().length < 8) return;

            setAddressLoading(true);
            try {
                const response = await fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_API_KEY}`
                );
                const data = await response.json();

                if (data.status === "OK" && data.results?.[0]?.geometry?.location) {
                    const { lat, lng } = data.results[0].geometry.location;
                    setFieldValue('Latitude', lat.toFixed(7));
                    setFieldValue('Longitude', lng.toFixed(7));
                    setMarker({ lat, lng });
                    await validateForm();
                }
                //  else {
                //     notifyError("Could not find coordinates for this address");
                // }
            } catch (err) {
                console.error("Forward geocoding error:", err);
                // notifyError("Error fetching coordinates. Please try again.");
            } finally {
                setAddressLoading(false);
            }
        }, 900),
        [setFieldValue]
    );

    // ── Reverse Geocoding: Lat/Lng → Address ────────────────────────────────
    const fetchAddressFromLatLng = useCallback(async (lat, lng) => {
        if (!lat || !lng) return;

        setAddressLoading(true);
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
            );
            const data = await response.json();

            if (data.status === "OK" && data.results?.[0]) {
                const formatted = data.results[0].formatted_address;
                setFieldValue('Address', formatted);
                setSelectedInfo({ lat, lng, address: formatted });
                setMarker({ lat, lng });
                await validateForm();
            } else {
                notifyError("Could not fetch address for these coordinates");
            }
        } catch (err) {
            console.error("Reverse geocoding error:", err);
            notifyError("Error fetching address. Please try again.");
        } finally {
            setAddressLoading(false);
        }
    }, [setFieldValue]);

    // ── Get current location ────────────────────────────────────────────────
    const getCurrentLocation = useCallback(() => {

        if (!navigator.geolocation) {
            notifyError("Geolocation is not supported by your browser");
            return;
        }

        setLocationLoading(true);
        setAddressLoading(true);

        navigator.geolocation.getCurrentPosition(
            async ({ coords: { latitude, longitude } }) => {
                try {
                    setFieldValue('Latitude', latitude.toFixed(7));
                    setFieldValue('Longitude', longitude.toFixed(7));
                    setMarker({ lat: latitude, lng: longitude });

                    await fetchAddressFromLatLng(latitude, longitude);
                } catch (e) {
                    console.error(e);
                    notifyError("Failed to fetch current location details");
                } finally {
                    setLocationLoading(false);
                    setAddressLoading(false);
                }
            },
            (err) => {
                notifyError("Location error: " + err.message);
                setLocationLoading(false);
                setAddressLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, [fetchAddressFromLatLng, setFieldValue]);

    // Auto-fetch on mount if empty
    useEffect(() => {
        if ((!values.Latitude || !values.Longitude) && !values.Address) {
            getCurrentLocation();
        }
    }, [getCurrentLocation, showMapModal]);

    // Two-way sync watchers
    useEffect(() => {
        if (values.Address?.length > 10) fetchLatLngFromAddress(values.Address);
    }, []);

    useEffect(() => {
        if (values.Latitude && values.Longitude && !isNaN(+values.Latitude) && !isNaN(+values.Longitude)) {
            fetchAddressFromLatLng(values.Latitude, values.Longitude);
        }
    }, []);

    // ── Handlers for manual input ───────────────────────────────────────────
    const handleAddressChange = (e) => {
        const address = e.target.value?.trim() || "";
        setFieldValue("Address", e.target.value);

        if (addressTimer) clearTimeout(addressTimer);

        if (!address) {
            setFieldValue("Latitude", "");
            setFieldValue("Longitude", "");
            return;
        }



        if (address.length > 5) {
            const timer = setTimeout(() => {
                fetchLatLngFromAddress(address);
            }, 800);
            setAddressTimer(timer);
        }
    };

    const handleLatitudeChange = (e) => {
        const inputValue = e.target.value;

        // Allow: empty, negative sign at start, digits, one decimal point
        const regex = /^-?\d*\.?\d*$/;

        // If inputValue is valid according to regex → allow update
        if (inputValue !== '' && !regex.test(inputValue)) {
            return
        }
        const newLat = e.target.value.trim();

        setFieldValue("Latitude", newLat);
        if (newLat == "") {
            setFieldValue("Address", "");

        }

        if (latLngTimer) clearTimeout(latLngTimer);

        if (newLat && values.Longitude && !isNaN(+newLat) && !isNaN(+values.Longitude)) {
            const timer = setTimeout(() => {
                fetchAddressFromLatLng(+newLat, +values.Longitude);
            }, 600);
            setLatLngTimer(timer);
        }
    };

    const handleLongitudeChange = (e) => {
        const inputValue = e.target.value;

        // Allow: empty, negative sign at start, digits, one decimal point
        const regex = /^-?\d*\.?\d*$/;

        // If inputValue is valid according to regex → allow update
        if (inputValue !== '' && !regex.test(inputValue)) {
            return
        }
        const newLng = e.target.value.trim();
        setFieldValue("Longitude", newLng);
        if (newLng == "") {
            setFieldValue("Address", "");

        }
        if (latLngTimer) clearTimeout(latLngTimer);

        if (newLng && values.Latitude && !isNaN(+newLng) && !isNaN(+values.Latitude)) {
            const timer = setTimeout(() => {
                fetchAddressFromLatLng(+values.Latitude, +newLng);
            }, 600);
            setLatLngTimer(timer);
        }
    };

    // ── Map Interaction Handler ─────────────────────────────────────────────
    const handleMapInteraction = (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        setFieldValue('Latitude', lat.toFixed(7));
        setFieldValue('Longitude', lng.toFixed(7));
        setMarker({ lat, lng });

        fetchAddressFromLatLng(lat, lng);
        setShowInfoWindow(true);
    };
    const handleOnCloseModel = () => {
        if (!values.Latitude && !values.Longitude && !values.Address) {
            getCurrentLocation();
        }
        setShowMapModal(false)

    }
    const inputBaseClasses = 'w-full h-12 px-4 rounded-lg bg-slate-50 dark:bg-slate-800 text-[#0d141b] dark:text-white placeholder:text-slate-400 transition-all outline-none';
    const inputFocusClasses = 'focus:ring-2 focus:ring-primary/20 focus:border-primary';
    const inputErrorClasses = 'border-red-500';
    const inputNormalBorder = 'border border-slate-300 dark:border-slate-600';

    if (!isLoaded) return <div className="text-center py-8">Loading Google Maps...</div>;

    return (
        <>
            {/* Location Details Section */}
            <div className="p-6 md:p-8 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/20">
                <div className="flex gap-2 items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                            <span className="material-symbols-outlined">location_on</span>
                        </div>
                        <h3 className="text-lg font-bold text-[#0d141b] dark:text-white">{t('locationDetails')}</h3>
                    </div>

                    <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={locationLoading}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${locationLoading ? 'bg-slate-200 text-slate-600 cursor-not-allowed' : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200'
                            }`}
                    >
                        {locationLoading ? (
                            <>
                                <span className="material-symbols-outlined animate-spin">refresh</span>
                                Detecting...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined">my_location</span>
                                Use Current Location
                            </>
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Address */}
                    <div className="col-span-1 md:col-span-2 flex flex-col gap-2 relative">
                        <label className="text-xs font-bold text-[#0d141b] dark:text-slate-300">
                            {t('registeredAddress')} <span className="text-red-700">*</span>
                        </label>
                        <div className="relative">
                            <textarea
                                name="Address"
                                value={values.Address}
                                onChange={handleAddressChange}
                                onBlur={handleBlur}
                                className={`${inputBaseClasses.replace('h-12', 'min-h-[100px]')} ${inputFocusClasses} ${touched.Address && errors.Address ? inputErrorClasses : inputNormalBorder
                                    } resize-y pr-10`}
                                placeholder={t('registeredAddress')}
                            />
                            {(addressLoading || locationLoading) && (
                                <div className="absolute right-3 top-4">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            )}
                        </div>
                        {touched.Address && errors.Address && <p className="text-red-700 text-xs font-bold">{errors.Address}</p>}
                    </div>

                    {/* Coordinates + Map Button */}
                    <div className="col-span-1 md:col-span-2">
                        <label className="text-xs font-bold text-[#0d141b] dark:text-slate-300 mb-2 block">
                            {t('geoCoordinates')}
                        </label>
                        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                            <div className="flex-1 w-full relative">
                                <span className="absolute left-3 top-3 text-slate-400 material-symbols-outlined text-[20px]">north</span>
                                <input
                                    name="Latitude"
                                    value={values.Latitude}
                                    maxLength={20}
                                    onChange={handleLatitudeChange}
                                    onBlur={handleBlur}
                                    className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.Latitude && errors.Latitude ? inputErrorClasses : inputNormalBorder
                                        }`}
                                    placeholder={t('latitude')}
                                    type="text"
                                />
                                {touched.Latitude && errors.Latitude && <p className="text-red-700 text-xs font-bold mt-1">{errors.Latitude}</p>}
                            </div>

                            <div className="flex-1 w-full relative">
                                <span className="absolute left-3 top-3 text-slate-400 material-symbols-outlined text-[20px]">east</span>
                                <input
                                    name="Longitude"
                                    value={values.Longitude}
                                    maxLength={20}
                                    onChange={handleLongitudeChange}
                                    onBlur={handleBlur}
                                    className={`${inputBaseClasses} pl-10 ${inputFocusClasses} ${touched.Longitude && errors.Longitude ? inputErrorClasses : inputNormalBorder
                                        }`}
                                    placeholder={t('longitude')}
                                    type="text"
                                />
                                {touched.Longitude && errors.Longitude && <p className="text-red-700 text-xs font-bold mt-1">{errors.Longitude}</p>}
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowMapModal(true)}
                                className="w-full md:w-auto h-12 px-6 bg-primary text-white rounded-lg font-medium hover:bg-blue-600 flex items-center justify-center gap-2 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">map</span>
                                {t('selectOnMap')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Map Modal */}
            {showMapModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-[#1A2633] rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-[#0d141b] dark:text-white">{t('selectOnMap')}</h2>
                            {/* <button
                                type="button"
                                onClick={() => handleOnCloseModel()}
                                className="text-slate-500 hover:text-red-700 transition-colors"
                            >
                                <span className="material-symbols-outlined text-3xl">close</span>
                            </button> */}
                        </div>
                        <div className="flex-1">
                            <GoogleMap
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                center={
                                    values.Latitude && values.Longitude
                                        ? { lat: parseFloat(values.Latitude), lng: parseFloat(values.Longitude) }
                                        : suratCenter
                                }
                                zoom={values.Latitude && values.Longitude ? 18 : 12}
                                options={{
                                    disableDefaultUI: false,
                                    clickableIcons: false,
                                    gestureHandling: "greedy",
                                    zoomControl: true,
                                    mapTypeControl: false,
                                    streetViewControl: false,
                                    fullscreenControl: true
                                }}
                                onLoad={(map) => {
                                    mapRef.current = map;
                                }}
                                onClick={handleMapInteraction}
                            >
                                {marker && (
                                    <Marker
                                        position={marker}
                                        draggable={true}
                                        onDragEnd={handleMapInteraction}
                                        onClick={() => setShowInfoWindow(true)}
                                    // Optional custom icon
                                    // icon={{ url: '/path/to/pin.png', scaledSize: new window.google.maps.Size(30, 30) }}
                                    />
                                )}

                                {showInfoWindow && marker && selectedInfo && (
                                    <InfoWindow
                                        position={marker}
                                        onCloseClick={() => setShowInfoWindow(false)}
                                        options={{ pixelOffset: new window.google.maps.Size(0, -35) }}
                                    >
                                        <div style={{ width: 250, padding: '8px' }}>
                                            <div><strong>Latitude:</strong> {selectedInfo.lat}</div>
                                            <div><strong>Longitude:</strong> {selectedInfo.lng}</div>
                                            <div><strong>Address:</strong><br />{selectedInfo.address}</div>
                                            <button
                                                onClick={() => setShowMapModal(false)}
                                                className="btn btn-success btn-sm mt-2 w-100"
                                            >
                                                Use This Location
                                            </button>
                                        </div>
                                    </InfoWindow>
                                )}
                            </GoogleMap>
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 text-center">
                            <button
                                type="button"
                                onClick={() => { handleOnCloseModel() }}
                                className="px-8 py-3 bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                            >
                                {t('done')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RegistrationAddressFiller;